import { startOfWeek, endOfWeek, format } from "date-fns";

import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";

import AutocompleteFloatLabel from "../../../components/AutocompleteFloatLabel";
import DialogMessage from "../../../components/DialogMessage";
import DialogMessageProps from "../../../model/DialogMessageProps";
import Cartao from "../../../model/dto/Cartao";
import CartaoParams from "../../../model/params/CartaoParams";
import CartaoTipo from "../../../model/dto/CartaoTipo";
import {
    getOptionsTipoCartaoEnum,
    TipoCartaoEnum,
} from "../../../model/Enums/TipoCartaoEnum";
import { TypeDialogMessageEnum } from "../../../model/Enums/TypeDialogMessageEnum";
import SelectOption from "../../../model/SelectOption";
import CartoesService from "../../../service/Docente/CartoesService";
import ButtonStyles from "../../../styles/components/ButtonStyles";
import DialogStyles from "../../../styles/components/DialogStyles";
import GenericUtils from "../../../utils/GenericUtils";
import Professor from "../../../model/dto/Professor";
import {
    convertToClassHours,
    getClockHoursEnumFromNumber,
    HoraRelogio,
} from "../../../model/Enums/HoraRelogioEnum";
import {
    StatusCheckinEnum,
    StatusCheckinEnumLabel,
} from "../../../model/Enums/StatusCheckinEnum";

const { BtnPrimary } = ButtonStyles;
const { StyledContainerButton } = DialogStyles;

interface IFormAnticipationProps {
    docente: Professor;
    matriculaAdp: string;
    onCreate: (typeCard: TipoCartaoEnum) => void;
}

function FormAnticipation(props: IFormAnticipationProps) {
    const { instance } = useMsal();

    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);

    const [typeActivity, setTypeActivity] = useState(new Array<SelectOption>());
    const [typeActivityOptions, setTypeActivityOptions] = useState(
        new Array<SelectOption>()
    );
    const [activity, setActivity] = useState(new Array<SelectOption>());
    const [activityList, setActivityList] = useState(new Array<Cartao>());
    const [activityOptions, setActivityOptions] = useState(
        new Array<SelectOption>()
    );
    const [numberHours, setNumberHours] = useState(new Array<SelectOption>());
    const [numberHoursOptions, setNumberHoursOptions] = useState(
        new Array<SelectOption>()
    );

    const isNonSchoolHours = typeActivity.length
        ? [String(TipoCartaoEnum.HoraNaoLetiva)].includes(typeActivity[0].value)
        : false;

    const isFormValid = isNonSchoolHours
        ? !!typeActivity.length && !!activity.length && !!numberHours.length
        : !!typeActivity.length && !!activity.length;

    const getCartoes = async () => {
        const activitiesTypeList =
            Number(typeActivity[0]?.value) === 1
                ? [1, 3]
                : [Number(typeActivity[0]?.value)];

        const cartaoParams = GenericUtils.impl<CartaoParams>({
            tipoCartaoId: activitiesTypeList,
            matriculaAdp: props.matriculaAdp,
            atividadesAntecipacao: true,
        });

        const response = await CartoesService.getCartoes(
            instance,
            cartaoParams
        );

        if (GenericUtils.isSuccessStatus(response)) {
            setActivityList(response.data?.cartoes);
            setActivityOptions(
                response.data?.cartoes?.map((item: Cartao) => {
                    const modalidadeDescricao =
                        item.modalidade && item.modalidade.modalidadeDescricao
                            ? ` - ${item.modalidade.modalidadeDescricao}`
                            : "";
                    return {
                        value: item.cartaoAgendaId,
                        label: `${item.abrangencia?.nome}${modalidadeDescricao}`,
                        labelSecondary: item.titulo,
                    };
                })
            );
        } else {
            setActivityList([]);
            setActivityOptions([]);
            setDialogMessageProps(GenericUtils.getPropsDialogError(response));
        }
    };

    const handleChangeTypeActivity = () => {
        if (typeActivity.length) {
            getCartoes();
        } else {
            setActivity([]);
            setActivityList([]);
            setActivityOptions([]);
        }
    };

    const getClassHours = (card: Cartao) => {
        if (isNonSchoolHours) {
            return Number(numberHours[0]?.value);
        }

        const cardDuration = card.modalidade
            ? card.modalidade?.duracaoCartao
            : "";

        const clockHourEnum: HoraRelogio | null = getClockHoursEnumFromNumber(
            GenericUtils.getMinutes(cardDuration)
        );

        return convertToClassHours(clockHourEnum);
    };

    const getClockHours = (card: Cartao) => {
        if (isNonSchoolHours) {
            return numberHours[0]?.value === "60" ? 45 : 22;
        }

        const cardDuration = card.modalidade
            ? card.modalidade?.duracaoCartao
            : "";

        return GenericUtils.getMinutes(cardDuration);
    };

    const handleSubmit = async () => {
        if (!isFormValid) return;

        const cartao = activityList.find(
            (item) => item.cartaoAgendaId === activity[0].value
        );

        if (!cartao) return;

        const hoje = new Date();
        const dtInicioSemana = startOfWeek(hoje, { weekStartsOn: 1 });
        const dtFimSemana = endOfWeek(hoje, { weekStartsOn: 1 });

        // Formatando como yyyy-MM-dd
        // const dataInicioFormatada = format(dtInicioSemana, "yyyy-MM-dd");
        // const dataFimFormatada = format(dtFimSemana, "yyyy-MM-dd");

        cartao.cartaoAgendaId = undefined;
        cartao.origemCartaoId = undefined;
        cartao.data = undefined;
        cartao.dataHoraCheckin = undefined;
        cartao.statusCheckin = StatusCheckinEnumLabel.get(
            StatusCheckinEnum.AguardandoCheckin
        );
        cartao.horaInicio = undefined;
        cartao.horaFim = undefined;
        cartao.duracaoHoraAula = getClassHours(cartao);
        cartao.duracaoHoraRelogio = getClockHours(cartao);
        cartao.cartaoTipoOrigem = GenericUtils.impl<CartaoTipo>({
            cartaoTipoId: Number(cartao.cartaoTipo?.cartaoTipoId),
        });
        cartao.cartaoTipo = GenericUtils.impl<CartaoTipo>({
            cartaoTipoId: TipoCartaoEnum.Antecipacao,
        });
        // cartao.dataInicioVigencia = dataInicioFormatada;
        // cartao.dataFimVigencia = dataFimFormatada;

        const response = await CartoesService.salvarCartao(instance, cartao);

        if (GenericUtils.isSuccessStatus(response)) {
            setDialogMessageProps({
                show: true,
                type: TypeDialogMessageEnum.Success,
                title: "Cartão salvo com sucesso",
            });
        } else {
            setDialogMessageProps(GenericUtils.getPropsDialogError(response));
        }
    };

    const handleCloseDialogMessage = () => {
        if (dialogMessageProps.type === TypeDialogMessageEnum.Success)
            props.onCreate(TipoCartaoEnum.Antecipacao);

        setDialogMessageProps({} as DialogMessageProps);
    };

    const getNumbersOption = () => {
        return [
            GenericUtils.impl<SelectOption>({
                value: "30",
                label: "0.5",
            }),
            GenericUtils.impl<SelectOption>({
                value: "60",
                label: "1",
            }),
        ];
    };

    const getTypeActivityByGerarAgenda = (docente: Professor) => {
        if (docente.gerarAgenda) {
            return getOptionsTipoCartaoEnum().filter((item) =>
                [
                    String(TipoCartaoEnum.HoraLetiva),
                    String(TipoCartaoEnum.HoraNaoLetiva),
                ].includes(item.value)
            );
        }
        return getOptionsTipoCartaoEnum().filter((item) =>
            [String(TipoCartaoEnum.HoraLetiva)].includes(item.value)
        );
    };

    useEffect(() => {
        setTypeActivityOptions(getTypeActivityByGerarAgenda(props.docente));
        setNumberHoursOptions(getNumbersOption());
    }, []);

    useEffect(() => handleChangeTypeActivity(), [typeActivity]);

    const dialogMessage = dialogMessageProps.show && (
        <DialogMessage
            type={dialogMessageProps.type}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={dialogMessageProps.text}
            onClose={handleCloseDialogMessage}
        />
    );

    return (
        <>
            <Row>
                <Col>
                    <AutocompleteFloatLabel
                        label="Tipo de atividade"
                        options={typeActivityOptions}
                        selected={typeActivity}
                        onChange={setTypeActivity}
                        className="modal-create-card-autocomplete"
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <AutocompleteFloatLabel
                        label="Atividade"
                        options={activityOptions}
                        selected={activity}
                        onChange={setActivity}
                        labelSecondary
                        className="modal-create-card-autocomplete"
                    />
                </Col>
            </Row>
            {isNonSchoolHours && (
                <Row>
                    <Col>
                        <AutocompleteFloatLabel
                            label="Quantidade Hora/Aula"
                            options={numberHoursOptions}
                            selected={numberHours}
                            onChange={setNumberHours}
                            className="modal-create-card-autocomplete"
                        />
                    </Col>
                </Row>
            )}
            <StyledContainerButton className="mt-3">
                <BtnPrimary disabled={!isFormValid} onClick={handleSubmit}>
                    Criar
                </BtnPrimary>
            </StyledContainerButton>
            {dialogMessage}
        </>
    );
}

export default FormAnticipation;
